import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatRadioChange } from '@angular/material/radio'
import { combineLatest, filter, Subject, takeUntil } from 'rxjs'
import { KeyingSystemService } from '../../services/keying.system.service'
import { KeySystem } from '../../models/dev.test.models'
import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { ToastService } from '@overtur-monorepo/keying-shared'
import { ErrorService } from '../../services/error.service'

@Component({
  selector: 'app-reports-dialog',
  templateUrl: './reports-dialog.component.html',
  styleUrls: ['./reports-dialog.component.scss'],
  standalone: false,
})
export class ReportsDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()

  keySystem: KeySystem
  topMasterKey: MasterKey
  allSymbolsFromHierarchy: Set<string> = new Set()

  keySetSymbolsForm: FormGroup
  thruNotationForm: FormGroup
  orderNumberForm: FormGroup

  isSymbolsButtonEnabled = false
  isRangeButtonEnabled = false
  isOrderNumberEnabled = false

  currentSlide = 1
  title = 'Full Report'
  selectedReportOption = 'Full Report'

  symbols: string[] = []
  ranges: string[] = []
  thruNotations = []
  payload = {}

  focus = {
    keyName: false,
    prefix: false,
    from: false,
    to: false,
    suffix: false,
    orderNumber: false,
  }

  public errorMessages = {
    keySetSymbols: [{ type: 'required', message: 'A Name is required.' }],
    prefix: [{ type: 'required', message: 'A Prefix is required.' }],
    from: [{ type: 'required', message: 'A From value is required.' }],
    to: [{ type: 'required', message: 'A To value is required.' }],
    suffix: [{ type: 'required', message: 'A Suffix is required.' }],
  }

  constructor(
    private keyingSystemService: KeyingSystemService,
    private errorService: ErrorService,
    private toastService: ToastService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ReportsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.keySetSymbolsForm = this.fb.group({
      keySetSymbols: [''],
    })

    this.thruNotationForm = this.fb.group({
      prefix: [''],
      from: [''],
      to: [''],
      suffix: [''],
    })

    this.orderNumberForm = this.fb.group({
      orderNumber: [''],
    })
  }

  ngOnInit() {
    combineLatest([this.keyingSystemService.keySystem$.pipe(filter((x) => !!x))])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([keySystem]: [KeySystem]) => {
        this.keySystem = keySystem
        this.topMasterKey = this.keySystem.masterKeysKeying
        this.collectSymbols(this.topMasterKey)
        console.log('Symbols extracted from tree:', Array.from(this.allSymbolsFromHierarchy))
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  collectSymbols(key: MasterKey): void {
    this.allSymbolsFromHierarchy.add(key.keySetSymbol)

    if (key.children && key.children.length > 0) {
      for (const child of key.children) {
        this.collectSymbols(child)
      }
    }
  }

  onReportRadioSelect(event: MatRadioChange): void {
    this.currentSlide = 1
    this.selectedReportOption = event.value
    this.title = event.value || 'Select Report Type'
  }

  goToNextSlide() {
    this.currentSlide = 2
  }

  goToPreviousSlide() {
    this.currentSlide = 1
  }

  checkOrderNumberInput(): void {
    const orderNumber = this.orderNumberForm.get('orderNumber')
      ? this.orderNumberForm.get('orderNumber').value?.trim() || ''
      : ''
    this.isOrderNumberEnabled = orderNumber.trim() !== ''
  }

  checkSymbolsInput(): void {
    const symbols = this.keySetSymbolsForm.get('keySetSymbols')
      ? this.keySetSymbolsForm.get('keySetSymbols').value?.trim() || ''
      : ''
    this.isSymbolsButtonEnabled = symbols.trim() !== ''
  }

  checkRangeInput(): void {
    let from = this.thruNotationForm.get('from') ? this.thruNotationForm.get('from').value : null
    let to = this.thruNotationForm.get('to') ? this.thruNotationForm.get('to').value : null

    // If input is a letter, ensure it's only the first letter and uppercase
    if (from && this.isLetter(from.charAt(0))) {
      from = from.charAt(0).toUpperCase()
      this.thruNotationForm.get('from').setValue(from)
    }

    if (to && this.isLetter(to.charAt(0))) {
      to = to.charAt(0).toUpperCase()
      this.thruNotationForm.get('to').setValue(to)
    }

    if (from && to) {
      if (!isNaN(Number(from)) && !isNaN(Number(to))) {
        // Numeric range validation
        this.isRangeButtonEnabled = Number(from) <= Number(to)
      } else if (this.isLetter(from) && this.isLetter(to)) {
        // Letter range validation (comparing uppercase letters)
        this.isRangeButtonEnabled = from.charCodeAt(0) <= to.charCodeAt(0)
      } else {
        // Mixed types are not valid
        this.isRangeButtonEnabled = false
      }
    } else {
      this.isRangeButtonEnabled = false
    }
  }

  addKeySetSymbols(): void {
    const inputValue = this.keySetSymbolsForm.get('keySetSymbols')?.value
    if (inputValue) {
      const newSymbols = inputValue
        .split(',')
        .map((symbol) => symbol.trim().toUpperCase())
        .filter((symbol) => symbol)

      const invalidSymbols = this.validateSymbols(newSymbols)
      if (invalidSymbols.length > 0) {
        this.showInvalidSymbols(invalidSymbols)
      } else {
        this.symbols = Array.from(new Set([...this.symbols, ...newSymbols]))
        this.keySetSymbolsForm.get('keySetSymbols')?.setValue('')
        this.isSymbolsButtonEnabled = false
      }
    }
  }

  removeSymbol(symbol: string): void {
    const index = this.symbols.indexOf(symbol)
    if (index >= 0) {
      this.symbols.splice(index, 1)
    }
  }

  addRange(): void {
    const prefix = this.thruNotationForm.get('prefix') ? this.thruNotationForm.get('prefix').value?.trim() || '' : ''
    let from = this.thruNotationForm.get('from') ? this.thruNotationForm.get('from').value : null
    let to = this.thruNotationForm.get('to') ? this.thruNotationForm.get('to').value : null
    const suffix = this.thruNotationForm.get('suffix') ? this.thruNotationForm.get('suffix').value?.trim() || '' : ''

    // Final validation and formatting before adding
    if (from && this.isLetter(from.charAt(0))) {
      from = from.charAt(0).toUpperCase()
    }

    if (to && this.isLetter(to.charAt(0))) {
      to = to.charAt(0).toUpperCase()
    }

    // Validate range before updating
    if (from !== null && to !== null) {
      const isNumericRange = !isNaN(Number(from)) && !isNaN(Number(to))
      const isLetterRange = this.isLetter(from) && this.isLetter(to)

      if ((isNumericRange && Number(from) <= Number(to)) || (isLetterRange && from.charCodeAt(0) <= to.charCodeAt(0))) {
        this.updateRange(prefix, from, to, suffix)
      }
    }
  }

  updateRange(prefix, from, to, suffix) {
    const range = `${prefix.toUpperCase()}${from.toUpperCase()}${suffix.toUpperCase()}-${prefix.toUpperCase()}${to.toUpperCase()}${suffix.toUpperCase()}`
    const newSymbols = this.generateSymbolsFromRange(prefix, from, to, suffix)
    const invalidSymbols = this.validateSymbols(newSymbols)
    if (invalidSymbols.length > 0) {
      this.showInvalidSymbols(invalidSymbols)
    } else {
      this.ranges = Array.from(new Set([...this.ranges, range]))
      this.thruNotations.push({ range: range, prefix: prefix, from: from, to: to, suffix: suffix })
      this.thruNotationForm.reset()
      this.isRangeButtonEnabled = false
    }
  }

  generateSymbolsFromRange(prefix: string, from: string, to: string, suffix: string): string[] {
    const symbols: string[] = []

    // Ensure from and to are properly formatted
    if (!isNaN(Number(from)) && !isNaN(Number(to))) {
      // Numeric range
      const start = Number(from)
      const end = Number(to)

      for (let i = start; i <= end; i++) {
        symbols.push(`${prefix.toUpperCase()}${i.toString()}${suffix.toUpperCase()}`)
      }
    } else if (this.isLetter(from) && this.isLetter(to)) {
      // Letter range - always ensure single uppercase letters
      const start = from.charAt(0).toUpperCase().charCodeAt(0)
      const end = to.charAt(0).toUpperCase().charCodeAt(0)

      for (let i = start; i <= end; i++) {
        symbols.push(`${prefix.toUpperCase()}${String.fromCharCode(i)}${suffix.toUpperCase()}`)
      }
    }
    return symbols
  }

  isLetter(value: string): boolean {
    return /^[A-Za-z]$/.test(value)
  }

  handleContextMenuInput(): void {
    // Handle possible context menu input (paste events)
    // Ensure letter inputs are single characters and uppercase
    let from = this.thruNotationForm.get('from')?.value
    let to = this.thruNotationForm.get('to')?.value

    if (from && typeof from === 'string' && this.isLetter(from.charAt(0))) {
      from = from.charAt(0).toUpperCase()
      this.thruNotationForm.get('from').setValue(from)
    }

    if (to && typeof to === 'string' && this.isLetter(to.charAt(0))) {
      to = to.charAt(0).toUpperCase()
      this.thruNotationForm.get('to').setValue(to)
    }

    this.checkRangeInput()
  }

  removeRange(range: string): void {
    const index = this.ranges.indexOf(range)
    if (index >= 0) {
      this.ranges.splice(index, 1)
    }
    this.thruNotations = this.thruNotations.filter((item) => item.range !== range)
  }

  validateSymbols(symbolsToCheck: string[]): string[] {
    const invalidSymbols: string[] = []

    for (const symbol of symbolsToCheck) {
      let symbolToCheck = symbol

      if (symbol.startsWith('*')) {
        symbolToCheck = symbol.substring(1)
      }

      if (!this.allSymbolsFromHierarchy.has(symbolToCheck)) {
        invalidSymbols.push(symbol)
      }
    }

    return invalidSymbols
  }

  showInvalidSymbols(invalidSymbols: string[]): void {
    if (invalidSymbols.length > 0) {
      const errorMessage = `Symbols: ${invalidSymbols.join(', ')}`

      this.errorService.notify('Invalid Symbols', errorMessage)
      setTimeout(() => {
        this.toastService.close()
      }, 8000)
    }
  }

  EnableDisableGenerateReportButton(): boolean {
    if (this.currentSlide === 1 && this.selectedReportOption === 'Full Report') {
      return false
    } else if (this.currentSlide === 1 && this.selectedReportOption === 'Report using Order #') {
      return !this.isOrderNumberEnabled
    } else if (this.currentSlide === 2 && (this.symbols.length > 0 || this.ranges.length > 0)) {
      return false
    }
    return true
  }

  submitReportRequest() {
    if (this.currentSlide === 1 && this.selectedReportOption === 'Full Report') {
      this.payload = {
        reportTypeId: this.data.id,
        reportType: 'Full',
        keySetSymbols: '',
        thruNotations: [],
      }
    } else if (this.currentSlide === 1 && this.selectedReportOption === 'Report using Order #') {
      this.payload = {
        reportTypeId: this.data.id,
        reportType: 'Partial',
        orderNumber: this.orderNumberForm.get('orderNumber')?.value?.trim() || '',
        keySetSymbols: '',
        thruNotations: [],
      }
    } else if (this.currentSlide === 2) {
      this.payload = {
        reportTypeId: this.data.id,
        reportType: 'Partial',
        keySetSymbols: this.symbols.join(','),
        thruNotations: this.thruNotations.map(({ range, ...rest }) => rest),
      }
    }

    this.close()
  }

  cancel() {
    this.close()
  }

  close() {
    this.dialogRef.close(this.payload)
  }

  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }
}
